import React, { useState, useMemo } from 'react';
import { Box, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pendingSitesService, PendingSite } from '../services/pendingSitesService';

const PendingSitesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'PENDING' | 'ACCEPTED' | 'REJECTED'>('PENDING');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [publisherFilter, setPublisherFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Fetch pending sites
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pending-sites', activeTab, page, rowsPerPage, searchTerm],
    queryFn: () => pendingSitesService.getPendingSites({
      status: activeTab,
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
    }),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['pending-sites-stats'],
    queryFn: () => pendingSitesService.getStats(),
  });

  // Accept mutation
  const acceptMutation = useMutation({
    mutationFn: (id: string) => pendingSitesService.acceptSite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-sites'] });
      queryClient.invalidateQueries({ queryKey: ['pending-sites-stats'] });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => 
      pendingSitesService.rejectSite(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-sites'] });
      queryClient.invalidateQueries({ queryKey: ['pending-sites-stats'] });
      setRejectDialogOpen(false);
      setRejectingId(null);
      setRejectReason('');
    },
  });

  // Bulk accept mutation
  const bulkAcceptMutation = useMutation({
    mutationFn: (ids: string[]) => pendingSitesService.acceptBulk(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-sites'] });
      queryClient.invalidateQueries({ queryKey: ['pending-sites-stats'] });
      setSelectedIds([]);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => pendingSitesService.deletePendingSite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-sites'] });
      queryClient.invalidateQueries({ queryKey: ['pending-sites-stats'] });
    },
  });

  const allSites = data?.data?.pendingSites || [];
  const pagination = data?.data?.pagination;
  const stats = statsData?.data;

  // Frontend filtering for category and publisher
  const filteredSites = useMemo(() => {
    let filtered = allSites;
    if (categoryFilter) {
      filtered = filtered.filter((site: PendingSite) => site.category === categoryFilter);
    }
    if (publisherFilter) {
      filtered = filtered.filter((site: PendingSite) => site.publisher_name === publisherFilter);
    }
    return filtered;
  }, [allSites, categoryFilter, publisherFilter]);

  // Get unique categories and publishers for filters
  const uniqueCategories = useMemo(() => {
    const cats = new Set(allSites.map((s: PendingSite) => s.category));
    return Array.from(cats).sort();
  }, [allSites]);

  const uniquePublishers = useMemo(() => {
    const pubs = new Set(allSites.map((s: PendingSite) => s.publisher_name));
    return Array.from(pubs).sort();
  }, [allSites]);

  // Use backend pagination - display all sites returned from API
  const paginatedSites = filteredSites;
  const totalCount = pagination?.total || 0;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(paginatedSites.map((site: PendingSite) => site.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleAccept = (id: string) => {
    acceptMutation.mutate(id);
  };

  const handleRejectClick = (id: string) => {
    setRejectingId(id);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    if (rejectingId) {
      rejectMutation.mutate({ id: rejectingId, reason: rejectReason });
    }
  };

  const handleBulkAccept = () => {
    if (selectedIds.length > 0) {
      bulkAcceptMutation.mutate(selectedIds);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this pending site?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleTabChange = (newTab: 'PENDING' | 'ACCEPTED' | 'REJECTED') => {
    setActiveTab(newTab);
    setPage(0);
    setSelectedIds([]);
    setCategoryFilter('');
    setPublisherFilter('');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setTimeout(() => setRefreshing(false), 600);
  };

  const handlePrevPage = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (pagination && page < pagination.totalPages - 1) setPage(page + 1);
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <style>{`
        .pending-sites-container {
          max-width: 100%;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .refresh-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #666;
          transition: all 0.2s;
        }

        .refresh-btn:hover {
          background: #f5f5f5;
          border-color: #d0d0d0;
        }

        .refresh-btn.refreshing {
          opacity: 0.6;
          pointer-events: none;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: white;
          border-radius: 8px;
          padding: 20px 24px;
          border: 1px solid #e0e0e0;
          border-left: 4px solid;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .stat-card.pending {
          border-left-color: #ff9800;
        }

        .stat-card.accepted {
          border-left-color: #4caf50;
        }

        .stat-card.rejected {
          border-left-color: #f44336;
        }

        .stat-card.total {
          border-left-color: #2196f3;
        }

        .stat-label {
          font-size: 12px;
          color: #999;
          margin-bottom: 8px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #333;
        }

        .stat-value.pending {
          color: #ff9800;
        }

        .stat-value.accepted {
          color: #4caf50;
        }

        .stat-value.rejected {
          color: #f44336;
        }

        .stat-value.total {
          color: #2196f3;
        }

        .content-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .tab-bar {
          display: flex;
          border-bottom: 1px solid #e0e0e0;
          padding: 0 20px;
          gap: 24px;
          background: white;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 12px 0;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: #999;
          transition: all 0.2s;
          margin-bottom: -1px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .tab-btn:hover {
          color: #333;
        }

        .tab-btn.active {
          color: #333;
          border-bottom-color: #667eea;
        }

        .tab-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }

        .tab-badge.pending {
          background: #ff9800;
          color: white;
        }

        .tab-badge.accepted {
          background: #4caf50;
          color: white;
        }

        .tab-badge.rejected {
          background: #f44336;
          color: white;
        }

        .bulk-action-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: #f8f9ff;
          border-bottom: 1px solid #e8eaf6;
        }

        .bulk-action-text {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .bulk-accept-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .bulk-accept-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .bulk-accept-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .search-filter-bar {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          background: #fafafa;
          border-bottom: 1px solid #e0e0e0;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 280px;
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #bbb;
          font-size: 16px;
        }

        .search-input {
          width: 100%;
          padding: 9px 14px 9px 38px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 13px;
          background: white;
          transition: all 0.2s;
          color: #666;
        }

        .search-input::placeholder {
          color: #bbb;
        }

        .search-input:focus {
          outline: none;
          border-color: #999;
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
        }

        .filter-select {
          padding: 9px 32px 9px 14px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 13px;
          background: white;
          cursor: pointer;
          min-width: 160px;
          transition: all 0.2s;
          color: #666;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23999' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
        }

        .filter-select:focus {
          outline: none;
          border-color: #999;
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
        }

        .results-count {
          font-size: 13px;
          color: #999;
          padding: 0 8px;
        }

        .table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: #f8f9fa;
          padding: 14px 12px;
          text-align: left;
          font-size: 13px;
          font-weight: 600;
          color: #555;
          border-bottom: 2px solid #e0e0e0;
          white-space: nowrap;
        }

        .data-table td {
          padding: 14px 12px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
          color: #333;
        }

        .data-table tbody tr:hover {
          background: #f8f9ff;
        }

        .checkbox-cell {
          width: 40px;
          text-align: center;
        }

        .custom-checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #667eea;
        }

        .site-url {
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #667eea;
          font-weight: 500;
        }

        .action-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }

        .action-badge.create {
          background: #e3f2fd;
          color: #1976d2;
        }

        .action-badge.update {
          background: #fff3e0;
          color: #f57c00;
        }

        .price-cell {
          text-align: right;
          font-weight: 600;
          font-family: 'Courier New', monospace;
        }

        .savings-cell {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 4px;
          color: #2e7d32;
          font-weight: 600;
        }

        .publisher-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .publisher-name {
          font-weight: 500;
          color: #333;
        }

        .publisher-email {
          font-size: 11px;
          color: #999;
        }

        .metrics-cell {
          font-weight: 500;
          color: #666;
        }

        .category-cell {
          font-size: 12px;
          color: #666;
          text-transform: capitalize;
        }

        .date-cell {
          font-size: 12px;
          color: #999;
          white-space: nowrap;
        }

        .action-buttons {
          display: flex;
          gap: 6px;
          justify-content: center;
        }

        .action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 16px;
        }

        .action-btn.accept {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .action-btn.accept:hover {
          background: #2e7d32;
          color: white;
        }

        .action-btn.reject {
          background: #ffebee;
          color: #c62828;
        }

        .action-btn.reject:hover {
          background: #c62828;
          color: white;
        }

        .action-btn.delete {
          background: #ffebee;
          color: #c62828;
        }

        .action-btn.delete:hover {
          background: #c62828;
          color: white;
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 64px;
          color: #ddd;
          margin-bottom: 16px;
        }

        .empty-text {
          font-size: 16px;
          color: #999;
          margin: 0;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-top: 1px solid #f0f0f0;
        }

        .pagination-info {
          font-size: 14px;
          color: #666;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .rows-per-page {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #666;
        }

        .rows-select {
          padding: 6px 10px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .pagination-buttons {
          display: flex;
          gap: 4px;
        }

        .page-btn {
          padding: 6px 12px;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          color: #666;
          transition: all 0.2s;
        }

        .page-btn:hover:not(:disabled) {
          background: #f5f5f5;
          border-color: #667eea;
          color: #667eea;
        }

        .page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .error-alert {
          margin: 20px;
          padding: 16px;
          background: #ffebee;
          border-left: 4px solid #c62828;
          border-radius: 4px;
          color: #c62828;
          font-size: 14px;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div className="pending-sites-container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Pending Sites Approval</h1>
          <button 
            className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <svg 
              className={refreshing ? 'spin' : ''} 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M13.65 2.35C12.2 0.9 10.21 0 8 0C3.58 0 0.01 3.58 0.01 8C0.01 12.42 3.58 16 8 16C11.73 16 14.84 13.45 15.73 10H13.65C12.83 12.33 10.61 14 8 14C4.69 14 2 11.31 2 8C2 4.69 4.69 2 8 2C9.66 2 11.14 2.69 12.22 3.78L9 7H16V0L13.65 2.35Z" 
                fill="currentColor"
              />
            </svg>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card pending">
            <div className="stat-label">Pending</div>
            <div className="stat-value pending">{stats?.pending || 0}</div>
          </div>
          <div className="stat-card accepted">
            <div className="stat-label">Accepted</div>
            <div className="stat-value accepted">{stats?.accepted || 0}</div>
          </div>
          <div className="stat-card rejected">
            <div className="stat-label">Rejected</div>
            <div className="stat-value rejected">{stats?.rejected || 0}</div>
          </div>
          <div className="stat-card total">
            <div className="stat-label">Total</div>
            <div className="stat-value total">{stats?.total || 0}</div>
          </div>
        </div>

        {/* Content Card */}
        <div className="content-card">
          {/* Tab Bar */}
          <div className="tab-bar">
            <button 
              className={`tab-btn ${activeTab === 'PENDING' ? 'active' : ''}`}
              onClick={() => handleTabChange('PENDING')}
            >
              Pending
              {stats?.pending ? <span className="tab-badge pending">{stats.pending}</span> : null}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'ACCEPTED' ? 'active' : ''}`}
              onClick={() => handleTabChange('ACCEPTED')}
            >
              Accepted
              {stats?.accepted ? <span className="tab-badge accepted">{stats.accepted}</span> : null}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'REJECTED' ? 'active' : ''}`}
              onClick={() => handleTabChange('REJECTED')}
            >
              Rejected
              {stats?.rejected ? <span className="tab-badge rejected">{stats.rejected}</span> : null}
            </button>
          </div>

          {/* Bulk Action Bar */}
          {activeTab === 'PENDING' && selectedIds.length > 0 && (
            <div className="bulk-action-bar">
              <span className="bulk-action-text">{selectedIds.length} selected</span>
              <button 
                className="bulk-accept-btn"
                onClick={handleBulkAccept}
                disabled={bulkAcceptMutation.isPending}
              >
                ✓ Accept Selected
              </button>
            </div>
          )}

          {/* Search and Filter Bar */}
          <div className="search-filter-bar">
            <div className="search-box">
              <svg 
                className="search-icon" 
                width="14" 
                height="14" 
                viewBox="0 0 14 14" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M13 13L10.1 10.1M11.6667 6.33333C11.6667 9.27885 9.27885 11.6667 6.33333 11.6667C3.38781 11.6667 1 9.27885 1 6.33333C1 3.38781 3.38781 1 6.33333 1C9.27885 1 11.6667 3.38781 11.6667 6.33333Z" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder="Search by URL, publisher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              className="filter-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map(cat => (
                <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <select 
              className="filter-select"
              value={publisherFilter}
              onChange={(e) => setPublisherFilter(e.target.value)}
            >
              <option value="">All Publishers</option>
              {uniquePublishers.map(pub => (
                <option key={pub} value={pub}>{pub}</option>
              ))}
            </select>
          </div>

          {/* Results Count */}
          {paginatedSites.length > 0 && (
            <div style={{ padding: '12px 20px', background: 'white', borderBottom: '1px solid #f0f0f0' }}>
              <span className="results-count">Showing {totalCount} sites</span>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div className="error-alert">
              Failed to load pending sites. Please try again.
            </div>
          )}

          {/* Table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {activeTab === 'PENDING' && (
                    <th className="checkbox-cell">
                      <input
                        type="checkbox"
                        className="custom-checkbox"
                        checked={paginatedSites.length > 0 && selectedIds.length === paginatedSites.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </th>
                  )}
                  <th>Site URL</th>
                  <th>Action</th>
                  <th style={{ textAlign: 'right' }}>Old Price</th>
                  <th style={{ textAlign: 'right' }}>New Price</th>
                  <th style={{ textAlign: 'right' }}>Savings</th>
                  <th>Publisher</th>
                  <th>DA/DR</th>
                  <th>Category</th>
                  <th>Submitted</th>
                  {activeTab !== 'PENDING' && <th>Reviewed By</th>}
                  {activeTab === 'REJECTED' && <th>Reason</th>}
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSites.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'PENDING' ? 12 : activeTab === 'REJECTED' ? 14 : 13}>
                      <div className="empty-state">
                        <div className="empty-icon">📋</div>
                        <p className="empty-text">No {activeTab.toLowerCase()} sites found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedSites.map((site: PendingSite) => {
                    const effectiveActionType = site.action_type;
                    const actionLabel = pendingSitesService.getActionTypeLabel(effectiveActionType);
                    const effectiveOldPrice = site.old_price || site.matchedExistingSite?.base_price || null;
                    const effectivePriceDiff = effectiveOldPrice ? effectiveOldPrice - site.base_price : site.price_difference;
                    const priceDiff = pendingSitesService.formatPriceDifference(effectivePriceDiff);
                    
                    return (
                      <tr key={site.id}>
                        {activeTab === 'PENDING' && (
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              className="custom-checkbox"
                              checked={selectedIds.includes(site.id)}
                              onChange={() => handleSelectOne(site.id)}
                            />
                          </td>
                        )}
                        <td>
                          <div className="site-url" title={site.site_url}>
                            {site.site_url}
                          </div>
                        </td>
                        <td>
                          <span className={`action-badge ${effectiveActionType === 'CREATE' ? 'create' : 'update'}`}>
                            {effectiveActionType === 'CREATE' ? '+ ' : '↻ '}
                            {actionLabel.text}
                          </span>
                        </td>
                        <td className="price-cell">
                          {effectiveActionType === 'CREATE' ? '-' : (
                            site.old_price 
                              ? pendingSitesService.formatPrice(site.old_price) 
                              : site.matchedExistingSite?.base_price 
                                ? pendingSitesService.formatPrice(site.matchedExistingSite.base_price)
                                : '-'
                          )}
                        </td>
                        <td className="price-cell">
                          {pendingSitesService.formatPrice(site.base_price)}
                        </td>
                        <td>
                          {effectiveActionType === 'CREATE' ? (
                            <div style={{ textAlign: 'right' }}>-</div>
                          ) : (
                            effectivePriceDiff && effectivePriceDiff > 0 ? (
                              <div className="savings-cell">
                                ↓ {priceDiff.text}
                              </div>
                            ) : (
                              <div style={{ textAlign: 'right' }}>-</div>
                            )
                          )}
                        </td>
                        <td>
                          <div className="publisher-info">
                            <span className="publisher-name">{site.publisher_name}</span>
                            <span className="publisher-email">{site.publisher_email}</span>
                          </div>
                        </td>
                        <td className="metrics-cell">{site.da}/{site.dr}</td>
                        <td className="category-cell">{site.category.replace(/_/g, ' ')}</td>
                        <td className="date-cell">{formatDate(site.submitted_at)}</td>
                        {activeTab !== 'PENDING' && (
                          <td>
                            {site.reviewer ? `${site.reviewer.firstName} ${site.reviewer.lastName}` : '-'}
                          </td>
                        )}
                        {activeTab === 'REJECTED' && (
                          <td>
                            <div style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={site.rejection_reason || ''}>
                              {site.rejection_reason || '-'}
                            </div>
                          </td>
                        )}
                        <td>
                          <div className="action-buttons">
                            {activeTab === 'PENDING' ? (
                              <>
                                <button
                                  className="action-btn accept"
                                  onClick={() => handleAccept(site.id)}
                                  disabled={acceptMutation.isPending}
                                  title="Accept"
                                >
                                  ✓
                                </button>
                                <button
                                  className="action-btn reject"
                                  onClick={() => handleRejectClick(site.id)}
                                  disabled={rejectMutation.isPending}
                                  title="Reject"
                                >
                                  ✕
                                </button>
                              </>
                            ) : (
                              <button
                                className="action-btn delete"
                                onClick={() => handleDelete(site.id)}
                                disabled={deleteMutation.isPending}
                                title="Delete"
                              >
                                <svg 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 16 16" 
                                  fill="none" 
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path 
                                    d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333" 
                                    stroke="currentColor" 
                                    strokeWidth="1.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount} results
              </div>
              <div className="pagination-controls">
                <div className="rows-per-page">
                  <span>Rows per page:</span>
                  <select 
                    className="rows-select"
                    value={rowsPerPage}
                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="pagination-buttons">
                  <button 
                    className="page-btn"
                    onClick={handlePrevPage}
                    disabled={page === 0}
                  >
                    Previous
                  </button>
                  <button 
                    className="page-btn"
                    onClick={handleNextPage}
                    disabled={!pagination || page >= pagination.totalPages - 1}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Site</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason (Optional)"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRejectConfirm} 
            color="error" 
            variant="contained"
            disabled={rejectMutation.isPending}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PendingSitesPage;
